import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/opt/atlassian/pipelines/agent/build/platform/services/design-system-docs/src/__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/templates/page.tsx";
import perfectPairLight from './perfect-pair.light.png';
import perfectPairDark from './perfect-pair.dark.png';
import iconsLight from './icons.light.png';
import iconsDark from './icons.dark.png';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <p>{`This month we've rolled out our new system fonts to bring a fresh look to our interface. Our icons
got a brand new look and a better taxonomy.`}</p>
    <p>{`Read on for the highlights and the full list of updates.`}</p>
    <h2 {...{
      "id": "meet-atlassian-sans-and-mono"
    }}>{`Meet Atlassian Sans and Mono`}</h2>
    <p>{`We’ve built our `}<a parentName="p" {...{
        "href": "/foundations/typography"
      }}>{`new typography system`}</a>{` from the ground up, from the
introduction of tokens and text components and moving to enhancing legibility and usability through
the creation of a scale and refined system of styles.`}</p>
    <p>{`The update includes our refreshed system fonts `}<strong parentName="p">{`Atlassian Sans`}</strong>{` and `}<strong parentName="p">{`Atlassian Mono`}</strong>{`, a perfect
pair that shares the same baseline, similar stroke width and a similar x-height.`}</p>
    <p>{`The typography system is in Beta and available for Atlassian teams. It will be available for
everyone soon.`}</p>
    <img src={perfectPairLight} srcDark={perfectPairDark} alt="The small differences between Atlassian Sans and Atlassian Mono typography are highlighted, showing they are similar in baseline, stroke, width, and X-height." caption="Text in Atlassian Sans and Atlassian Mono highlighting their similarity in baseline, stroke width and X-height." />
    <h2 {...{
      "id": "icons-get-a-glow-up"
    }}>{`Icons get a glow up`}</h2>
    <p>{`The new-and-improved `}<a parentName="p" {...{
        "href": "/foundations/iconography"
      }}>{`iconography system`}</a>{` is key ingredient of our
`}<strong parentName="p">{`modern design language`}</strong>{`.`}</p>
    <p>{`The consistent icon sizing and spacing ensure a uniform look and feel across the platform. Our
`}<strong parentName="p">{`improved icon taxonomy`}</strong>{`, detailed usage information, and `}<strong parentName="p">{`icon explorer`}</strong>{` makes finding and
using icons much simpler.`}</p>
    <p>{`The consistent icon sizing and spacing work harmoniously with our new typography and other UI
elements, ensuring a uniform look and feel across the platform.`}</p>
    <p>{`Along with the gorgeous new icons, we’ve launched refined documentation and guidance, improved
tooling, and a clear process for Atlassians to contribute icons to the system.`}</p>
    <p>{`The iconography system is in Beta and available for Atlassian teams. It will be available for
everyone soon.`}</p>
    <img src={iconsLight} srDark={iconsDark} alt="Old icons are shown with more inconsistent visual weighting compared to the new icon example set, which is visually consistent and has a lighter stroke width." caption="Old iconography library icons versus new library icons in beta." />
    <h2 {...{
      "id": "latest-updates"
    }}>{`Latest updates`}</h2>
    <h3 {...{
      "id": "typography-changes"
    }}>{`Typography changes`}</h3>
    <h4 {...{
      "id": "figma"
    }}>{`Figma`}</h4>
    <ul>
      <li parentName="ul">{`View styles and components in legacy (original), modernized (updated) and refreshed (final) in
`}<a parentName="li" {...{
          "href": "https://go.atlassian.com/figma-libraries"
        }}>{`ADS Figma libraries (Atlassians only)`}</a>{`.`}</li>
      <li parentName="ul">{`Learn how to use the new system in our
`}<a parentName="li" {...{
          "href": "https://go.atlassian.com/ads-typography-playground"
        }}>{`typography Figma playground (Atlassians only)`}</a>{`.`}</li>
    </ul>
    <h4 {...{
      "id": "code"
    }}>{`Code`}</h4>
    <ul>
      <li parentName="ul">{`Use the updated `}<a parentName="li" {...{
          "href": "/components/heading/examples"
        }}>{`heading component`}</a>{` and
`}<a parentName="li" {...{
          "href": "/components/primitives/text"
        }}>{`text primitive`}</a>{`.`}</li>
      <li parentName="ul">{`Follow the
`}<a parentName="li" {...{
          "href": "https://go.atlassian.com/ads-type-migration"
        }}>{`typography system migration guide (Atlassians only)`}</a>{`.`}</li>
    </ul>
    <h4 {...{
      "id": "documentation"
    }}>{`Documentation`}</h4>
    <ul>
      <li parentName="ul">{`Review the `}<a parentName="li" {...{
          "href": "/foundations/typography"
        }}>{`updated typography style docs`}</a>{`.`}</li>
      <li parentName="ul">{`Reference the new `}<a parentName="li" {...{
          "href": "/components/tokens/all-tokens#font"
        }}>{`font tokens`}</a>{`.`}</li>
    </ul>
    <h3 {...{
      "id": "iconography-changes"
    }}>{`Iconography changes`}</h3>
    <h4 {...{
      "id": "figma-1"
    }}>{`Figma`}</h4>
    <ul>
      <li parentName="ul">{`Start using the new icons in the
`}<a parentName="li" {...{
          "href": "https://go.atlassian.com/ads-figma-icon-migration"
        }}>{`Beta icon migration guide (Atlassians only)`}</a>{`.`}</li>
    </ul>
    <h4 {...{
      "id": "code-1"
    }}>{`Code`}</h4>
    <ul>
      <li parentName="ul">{`Follow the `}<a parentName="li" {...{
          "href": "/components/icon/migration-guide"
        }}>{`migration guide for the new icon component`}</a>{`.`}</li>
      <li parentName="ul">{`Reference `}<a parentName="li" {...{
          "href": "/components/icon/examples"
        }}>{`icon code examples`}</a>{`.`}</li>
    </ul>
    <h4 {...{
      "id": "documentation-1"
    }}>{`Documentation`}</h4>
    <ul>
      <li parentName="ul">{`Find the icon you need using the
`}<a parentName="li" {...{
          "href": "https://atlassian.design/components/icon/icon-explorer"
        }}>{`icon explorer`}</a>{`.`}</li>
      <li parentName="ul">{`Review the updated `}<a parentName="li" {...{
          "href": "/foundations/iconography"
        }}>{`iconography style docs`}</a>{`.`}</li>
      <li parentName="ul">{`Follow
`}<a parentName="li" {...{
          "href": "https://go.atlassian.com/ads-icon-contribution"
        }}>{`guidance to contribute new icons (Atlassians only)`}</a>{`.`}</li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      